import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/common/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { rrhhInventarioService } from '../services/rrhhInventario';
import toast from 'react-hot-toast';
import {
    Shirt, Plus, Upload, Download, Search, Edit, X, Sparkles, Camera, Loader2,
} from 'lucide-react';

// ─── Modal: nuevo / editar producto ─────────────────────────────────────────

const ModalProducto = ({ item, onCerrar, onGuardado }) => {
    const esEdicion = !!item;
    const [sku, setSku] = useState(item?.sku || '');
    const [descripcion, setDescripcion] = useState(item?.descripcion || '');
    const [categoria, setCategoria] = useState(item?.categoria || '');
    const [talla, setTalla] = useState(item?.talla || '');
    const [unidad, setUnidad] = useState(item?.unidad || 'Unidad');
    const [stockInicial, setStockInicial] = useState('');
    const [vidaUtilDias, setVidaUtilDias] = useState(item?.vida_util_dias ?? '');
    const [fotoUrl, setFotoUrl] = useState(item?.foto_url || null);
    const [fotoPreview, setFotoPreview] = useState(item?.foto_url || null);
    const [analizando, setAnalizando] = useState(false);
    const [confianzaIA, setConfianzaIA] = useState(null);
    const [guardando, setGuardando] = useState(false);

    const handleFoto = async (file) => {
        if (!file) return;
        setFotoPreview(URL.createObjectURL(file));
        setAnalizando(true);
        setConfianzaIA(null);
        try {
            const sugerencia = await rrhhInventarioService.analizarFoto(file);
            if (sugerencia.descripcion) setDescripcion(sugerencia.descripcion);
            if (sugerencia.categoria) setCategoria(sugerencia.categoria);
            if (sugerencia.talla) setTalla(sugerencia.talla);
            if (sugerencia.foto_url) setFotoUrl(sugerencia.foto_url);
            setConfianzaIA(sugerencia.confianza || null);
            toast.success('La IA sugirió los datos del producto — revísalos antes de guardar');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo reconocer el producto. Completa los datos manualmente.');
        } finally {
            setAnalizando(false);
        }
    };

    const handleGuardar = async () => {
        if (!descripcion.trim()) { toast.error('La descripción es obligatoria'); return; }
        const payload = {
            sku: sku.trim() || null,
            descripcion: descripcion.trim(),
            categoria: categoria.trim() || null,
            talla: talla.trim() || null,
            unidad: unidad.trim() || 'Unidad',
            vida_util_dias: vidaUtilDias !== '' && Number(vidaUtilDias) > 0 ? Number(vidaUtilDias) : null,
        };
        setGuardando(true);
        try {
            if (esEdicion) {
                await rrhhInventarioService.actualizarItem(item.id, payload);
                toast.success('Producto actualizado');
            } else {
                await rrhhInventarioService.crearItem({
                    ...payload,
                    foto_url: fotoUrl,
                    stock_inicial: stockInicial !== '' ? Number(stockInicial) : null,
                });
                toast.success('Producto creado');
            }
            onGuardado();
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al guardar el producto');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal title={esEdicion ? 'Editar producto' : 'Nuevo producto'} onClose={onCerrar} size="sm">
            <div className="space-y-4">
                {!esEdicion && (
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50">
                        {fotoPreview ? (
                            <img src={fotoPreview} alt="Producto" className="h-32 mx-auto object-contain rounded-lg mb-2" />
                        ) : (
                            <Camera className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                        )}
                        <label className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-semibold rounded-lg cursor-pointer hover:bg-blue-100">
                            <Sparkles className="h-4 w-4 mr-1.5" />
                            {analizando ? 'Reconociendo...' : 'Reconocer con IA'}
                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.heic"
                                className="hidden"
                                disabled={analizando}
                                onChange={(e) => handleFoto(e.target.files[0] || null)}
                            />
                        </label>
                        {analizando && <Loader2 className="h-4 w-4 inline-block animate-spin ml-2 text-blue-600" />}
                        {confianzaIA && (
                            <p className="text-xs text-gray-400 mt-2">Confianza de la IA: {confianzaIA} — revisá los campos antes de guardar</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Opcional: sacale una foto para precargar los datos</p>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">SKU</label>
                    <input
                        type="text"
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        placeholder="Se genera automáticamente si lo dejas vacío"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción</label>
                    <input
                        type="text"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Categoría</label>
                        <input
                            type="text"
                            value={categoria}
                            onChange={(e) => setCategoria(e.target.value)}
                            placeholder="Ej: Uniforme, EPP"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Talla</label>
                        <input
                            type="text"
                            value={talla}
                            onChange={(e) => setTalla(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Unidad de medida</label>
                    <input
                        type="text"
                        value={unidad}
                        onChange={(e) => setUnidad(e.target.value)}
                        placeholder="Ej: Unidad, Par, Caja (no es la cantidad)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Caducidad (días desde la entrega)</label>
                    <input
                        type="number"
                        min="1"
                        value={vidaUtilDias}
                        onChange={(e) => setVidaUtilDias(e.target.value)}
                        placeholder="Ej: 180 — vacío si no caduca"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        Cada entrega de este producto vence a los días indicados y aparece en "Caducidad EPP" para renovarla.
                    </p>
                </div>

                {!esEdicion && (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Stock inicial (opcional)</label>
                        <input
                            type="number"
                            min="0"
                            value={stockInicial}
                            onChange={(e) => setStockInicial(e.target.value)}
                            placeholder="Ej: 10 — cuántas unidades ya tenés en almacén"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            Si tenés stock de este producto, poné la cantidad acá. Se guarda como una entrada
                            en el kardex. Si lo dejás vacío, el producto arranca en 0 y después usás "Movimientos".
                        </p>
                    </div>
                )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                </button>
                <Button onClick={handleGuardar} loading={guardando} disabled={guardando}>
                    Guardar
                </Button>
            </div>
        </Modal>
    );
};

// ─── Modal: cargar Excel ─────────────────────────────────────────────────────

const ModalCargarExcel = ({ onCerrar, onCargado }) => {
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleSubir = async () => {
        if (!archivo) { toast.error('Selecciona un archivo Excel'); return; }
        setSubiendo(true);
        try {
            const res = await rrhhInventarioService.cargarExcel(archivo);
            setResultado(res);
            toast.success(res.message || 'Archivo procesado');
            onCargado();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al procesar el archivo');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <Modal title="Cargar catálogo (Excel)" onClose={onCerrar} size="sm">
            <div className="space-y-4">
                <button
                    onClick={() => rrhhInventarioService.descargarPlantilla()}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                    <Download className="h-4 w-4 mr-1" /> Descargar plantilla de ejemplo
                </button>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Archivo Excel</label>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-400 mt-1">Columnas: sku (opcional), descripcion, categoria, talla, unidad, vida_util_dias (caducidad, opcional), stock_inicial (opcional).</p>
                </div>
                {resultado && (
                    <div className="text-sm bg-gray-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-700">Creados: {resultado.creados}</p>
                        {resultado.errores?.length > 0 && (
                            <ul className="mt-2 text-xs text-red-600 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                                {resultado.errores.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cerrar
                </button>
                <Button onClick={handleSubir} loading={subiendo} disabled={subiendo || !archivo}>
                    <Upload className="h-4 w-4 inline mr-1" /> Subir
                </Button>
            </div>
        </Modal>
    );
};

// ─── Página principal ───────────────────────────────────────────────────────

const RRHHCatalogoPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [descargando, setDescargando] = useState(false);
    const [modalProducto, setModalProducto] = useState(null); // null cerrado, {} nuevo, {...} editar
    const [modalExcelAbierto, setModalExcelAbierto] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            setItems(await rrhhInventarioService.listarItems(busqueda || null));
        } catch (err) {
            toast.error('Error al cargar el catálogo');
        } finally {
            setLoading(false);
        }
    }, [busqueda]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleEliminar = async (id) => {
        if (!window.confirm('¿Desactivar este producto del catálogo?')) return;
        try {
            await rrhhInventarioService.eliminarItem(id);
            toast.success('Producto desactivado');
            cargar();
        } catch (err) {
            toast.error('Error al desactivar el producto');
        }
    };

    const descargarInventario = async () => {
        setDescargando(true);
        try {
            // Descarga lo mismo que muestra la tabla (respeta la búsqueda actual).
            await rrhhInventarioService.exportarInventarioExcel(busqueda.trim() || null);
        } catch (err) {
            toast.error('No se pudo descargar el inventario');
        } finally {
            setDescargando(false);
        }
    };

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="bg-pink-100 p-2.5 rounded-xl">
                            <Shirt className="h-6 w-6 text-pink-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">RRHH · Catálogo</h1>
                            <p className="text-sm text-gray-500">Uniformes y equipos entregados al personal</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={descargarInventario}
                            disabled={descargando}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <Download className="h-4 w-4 mr-2" /> {descargando ? 'Descargando...' : 'Descargar inventario'}
                        </button>
                        <button
                            onClick={() => setModalExcelAbierto(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            <Upload className="h-4 w-4 mr-2" /> Cargar Excel
                        </button>
                        <Button onClick={() => setModalProducto({})}>
                            <Plus className="h-4 w-4 inline mr-1" /> Nuevo producto
                        </Button>
                    </div>
                </div>

                <div className="relative mb-4 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar por SKU o descripción..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                {loading ? (
                    <Loading message="Cargando catálogo..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Producto</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">SKU</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Categoría</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Talla</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Caducidad</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Stock</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Sin productos</td></tr>
                                )}
                                {items.map((it) => (
                                    <tr key={it.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center space-x-3">
                                                {it.foto_url ? (
                                                    <img src={it.foto_url} alt={it.descripcion} className="h-10 w-10 rounded-lg object-cover" />
                                                ) : (
                                                    <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                        <Shirt className="h-5 w-5 text-gray-300" />
                                                    </div>
                                                )}
                                                <span className="font-medium text-gray-900">{it.descripcion}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{it.sku}</td>
                                        <td className="px-4 py-3 text-gray-600">{it.categoria || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600">{it.talla || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600">{it.vida_util_dias ? `${it.vida_util_dias} días` : '—'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                it.stock_actual <= 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                            }`}>
                                                {it.stock_actual} {it.unidad}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => setModalProducto(it)} className="text-gray-400 hover:text-blue-600 mr-2" title="Editar">
                                                <Edit className="h-4 w-4 inline" />
                                            </button>
                                            <button onClick={() => handleEliminar(it.id)} className="text-gray-400 hover:text-red-600" title="Desactivar">
                                                <X className="h-4 w-4 inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modalProducto !== null && (
                <ModalProducto item={modalProducto.id ? modalProducto : null} onCerrar={() => setModalProducto(null)} onGuardado={cargar} />
            )}
            {modalExcelAbierto && (
                <ModalCargarExcel onCerrar={() => setModalExcelAbierto(false)} onCargado={cargar} />
            )}
        </Layout>
    );
};

export default RRHHCatalogoPage;
